import { Button, Typography } from '@mui/material'
import React, { FC, forwardRef, useEffect, useState } from 'react'
import {
  InformationModal,
  TextInputField,
  TextInputFieldProps,
} from 'utoniq-ui'

import {
  ArtistUpdatePostCategoryInput,
  PostCategoryDetail,
} from '@/clients/utoniq-core/schema'
import { tLabel, useMessage, useTranslation } from '@/core'
import { CellFrame } from '@/core/architecture'
import { captureError } from '@/core/errors'
import { Form, handleError, useForm, UseFormProps } from '@/core/form'

export type PostCategoryUpdateValues = {
  name: string
  description: string
}

export type UpdatePostCategoryModalProps = {
  category: PostCategoryDetail
  open: boolean
  onClose: () => void
  onUpdateCategory: (inputs: ArtistUpdatePostCategoryInput) => Promise<void>
}

const schema: UseFormProps['schema'] = o => ({
  name: o.string().max(20).label(tLabel('consoleCreateCategory.name')),
  description: o
    .string()
    .max(10000)
    .label(tLabel('consoleCreateCategory.description')),
})

export const UpdatePostCategoryModal: FC<UpdatePostCategoryModalProps> = ({
  category,
  open,
  onClose,
  onUpdateCategory,
}) => {
  const { t } = useTranslation('consolePostCategories')
  const { showMessage, showError } = useMessage()
  const [loading, setLoading] = useState(false)
  const {
    handleSubmit,
    formState: { errors, isValid },
    register,
    setError,
    getValues,
    reset,
  } = useForm<PostCategoryUpdateValues>({
    schema,
    defaultValues: {
      name: category.name,
      description: category.description,
    },
  })

  useEffect(() => {
    reset({
      name: category.name,
      description: category.description,
    })
  }, [category.name, category.description])

  const handleClose = () => {
    reset()
    onClose()
  }

  const onUpdatePostCategory = async () => {
    setLoading(true)
    const { ...inputs } = getValues()
    return onUpdateCategory({ ...inputs, categoryId: category.id })
      .then(() => {
        showMessage(t('completePostCreate'))
        handleClose()
      })
      .catch(e => {
        captureError(e, 'consoleCategoryCreate')
        handleError<PostCategoryUpdateValues>(e, setError, showError)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <InformationModal
      open={open}
      onClose={() => handleClose()}
      innerSx={{
        width: '80vw',
        maxWidth: 500,
      }}
      title={t('updateCategoryModalTitle')}
      content={
        <Form onSubmit={handleSubmit(onUpdatePostCategory)} sx={{ pt: 6 }}>
          <TextInputCell
            type="text"
            textFieldProps={{
              variant: 'standard',
              InputProps: {
                sx: {
                  bgcolor: 'background.default',
                  px: 4,
                  py: 3,
                },
                disableUnderline: true,
              },
            }}
            titleProps={{
              variant: 'h5',
              color: 'text.primary',
              textAlign: 'left',
            }}
            error={errors.name}
            {...register('name')}
          />
          <TextInputCell
            type="textarea"
            textFieldProps={{
              variant: 'standard',
              multiline: true,
              rows: 5,
              InputProps: {
                sx: {
                  bgcolor: 'background.default',
                  px: 4,
                  py: 3,
                },
                disableUnderline: true,
              },
            }}
            titleProps={{
              children: (
                <>
                  <Typography
                    variant="h5"
                    sx={{
                      color: 'text.primary',
                      textAlign: 'left',
                    }}
                  >
                    {t('descriptionTitle')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      textAlign: 'left',
                    }}
                  >
                    {t('descriptionDetail')}
                  </Typography>
                </>
              ),
            }}
            error={errors.description}
            {...register('description')}
          />
          <Button
            type="submit"
            size="large"
            color="primary"
            sx={{ py: 4, mb: 5, minWidth: 212 }}
            onClick={() => handleSubmit(onUpdatePostCategory)}
            loading={loading}
            disabled={loading}
          >
            {t('updateCategory')}
          </Button>
        </Form>
      }
      cancelText={t('cancel')}
    />
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('consolePostCategories')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)
